import React, { useState, useEffect } from 'react';
import Utils from '../util/Utils.jsx';
import { Tour } from '@reactour/tour';
import { Tab, Tabs } from 'react-bootstrap';
import AccountSetup from '../components/AccountSetup.jsx';
import ProjectSetup from '../components/ProjectSetup.jsx';
import SetupTopNav from '../components/SetupTopNav.jsx';
import AddProjectModal from '../components/AddProjectModal.jsx';
import EditProjectFileModal from '../components/EditProjectFileModal.jsx';
import { trackPageView, trackEvent } from '../util/Analytics.jsx';
import { getDatasets } from '../offline-features/getDatasets.js';
import { shouldUseDevMockProjects } from '../util/devMockProjects.js';

import { useStateValue } from '../context/state.jsx';
import { setDatasets, setDatasetInfo } from '../context/datasets.js';
import { closeDataset } from '../context/openDatasets.js';
import { setTourInfo } from '../context/tourInfo.js';
import { useMatch } from 'react-router-dom';
import ProjectHub from '../components/ProjectHub.jsx';

const SetupPage = () => {
	const [permissions, setPermissions] = useState({});
	const [editingDatasetId, setEditingDatasetId] = useState(null);
	const [editingType, setEditingType] = useState(null);
	const [activeTab, setActiveTab] = useState(1);
	const [tourId, setTourId] = useState(null);
	const [tourSteps, setTourSteps] = useState([]);
	const [isTourOpen, setIsTourOpen] = useState(false);
	const [currentTourStep, setCurrentTourStep] = useState(0);
	const [permissionsLoading, setPermissionsLoading] = useState(true);
	const [datasetsLoading, setDatasetsLoading] = useState(true);

	const [{ tourInfo, isOnline, currentDataset, datasets, showAddProjectModal }, dispatch] = useStateValue();
	const hubMatch = useMatch('/setup/projects/:datasetId');
	const hubDatasetId = hubMatch?.params?.datasetId || null;

	const closeTour = () => {
		setIsTourOpen(false);
		dispatch(setTourInfo(tourId));
		Utils.ajax('updateTourInfo', { tourId }, dispatch);
	};

	const handleSetTourOpen = (value) => {
		const next = typeof value === 'function' ? value(isTourOpen) : value;
		if (next) {
			setIsTourOpen(true);
		} else {
			closeTour();
		}
	};

	const loadDatasets = () => {
		setDatasetsLoading(true);
		getDatasets(dispatch)
			.then(result => {
				if (result.success && result.datasets) {
					dispatch(setDatasets(result.datasets));
				}
			})
			.catch(() => {})
			.finally(() => {
				setDatasetsLoading(false);
			});
	};

	const getPermissions = () => {
		if (shouldUseDevMockProjects()) {
			setPermissionsLoading(false);
			return;
		}
		setPermissionsLoading(true);
		Utils.ajaxAsync('getPermissions', {}, null, { suppressErrorAlert: true })
			.then(result => {
				if (result.success) {
					setPermissions(result.permissions || {});
				}
			})
			.catch(() => {})
			.finally(() => {
				setPermissionsLoading(false);
			});
	};

	const setUserPermission = (userId, datasetId, permissionId, permissionSetting) => {
		// Keep values as strings so PHP always receives a complete urlencoded body
		// (booleans have caused empty $_POST / missing clientVersion through some proxies).
		const postData = {
			userId: String(userId),
			datasetId: String(datasetId),
			permissionId: String(permissionId),
			permissionSetting: permissionSetting ? 'true' : 'false'
		};
		Utils.ajax('updateUserPermission', postData, dispatch, result => {
			if (result.success) {
				getPermissions();
			}
		});
	};

	const setEditingDataset = (nextEditingDatasetId, nextEditingType) => {
		setEditingDatasetId(nextEditingDatasetId);
		setEditingType(nextEditingType);
	};

	const updateAreaProperty = (datasetId, areaProperty) => {
		const postData = {
			datasetId,
			areaProperty
		};
		updateDatasetMetadata(datasetId, 'areaProperty', areaProperty);
		Utils.ajax('updateAreaProperty', postData, dispatch);
	};

	const deleteProject = datasetId => {
		trackEvent('project', 'click', 'delete dataset', datasetId);
		Utils.ajax('deleteDataset', { dataset: datasetId }, dispatch, result => {
			if (result.success) {
				if (currentDataset === datasetId) {
					dispatch(setDatasetInfo(null));
				}
				dispatch(closeDataset(datasetId));
				getDatasets(dispatch).then(next => {
					if (next.success) {
						dispatch(setDatasets(next.datasets));
					}
				}).catch(() => {});
			}
		});
	};

	const updateDatasetMetadata = (datasetId, propertyName, value) => {
		trackEvent('project', 'click', 'update project metadata');
		const validPropertyNames = {
			StartDate: true,
			EndDate: true,
			RemainingDurationUnit: true,
			AllowNotStartDurationChanges: true,
			DisplayTime: true,
			AllowOffline: true,
			areaProperty: true,
			ShowGanttHolidays: true
		};
		if (!(propertyName in validPropertyNames) || !datasets?.[datasetId]) {
			return;
		}
		const datasetsCopy = { ...datasets };
		datasetsCopy[datasetId] = {
			...datasetsCopy[datasetId],
			[propertyName]: value
		};
		dispatch(setDatasets(datasetsCopy));
	};

	useEffect(() => {
		trackPageView();
		getPermissions();
		loadDatasets();
	}, []);

	useEffect(() => {
		setTourId(4);
		setTourSteps([{
			selector: '#setupPageTabs-tab-2',
			content: 'Manage your projects, project users, and project permissions here.',
			action: () => {
				if (activeTab !== 2) {
					setActiveTab(2);
				}
			}
		}, {
			selector: '.setupProjectsPane',
			content: 'Configure opens a hub for files, versions, and people. One project is open at a time.'
		}, {
			selector: '.setupProjectListCard',
			content: 'Open a project from the library, or configure it. Close is on the project that is open.'
		}]);

		if (hubDatasetId && activeTab !== 2) {
			setActiveTab(2);
		}

		if (!hubDatasetId && tourId && tourInfo && typeof tourInfo === 'object' && !(tourId in tourInfo)) {
			setCurrentTourStep(0);
			setIsTourOpen(true);
		}
	}, [tourId, tourInfo, activeTab, hubDatasetId]);

	return (
		<div className="setupPageShell">
			<SetupTopNav />
			<div className="belowTopNavContent setupPageContent">
				<header className="setupPageHero">
					<div>
						<p className="setupEyebrow">SiteWalker</p>
						<h1>{hubDatasetId ? 'Project hub' : 'Setup'}</h1>
						<p className="setupLead">
							{hubDatasetId
								? 'Administer one project while others remain open in the app session.'
								: 'Account security and project configuration in one place.'}
						</p>
					</div>
				</header>

				{(permissionsLoading || datasetsLoading) && (
					<div className="setupInlineAlert setupInlineAlert-info" role="status">
						{datasetsLoading ? 'Loading projects…' : 'Loading project permissions…'}
					</div>
				)}

				<div className="setupTabsCard">
					{hubDatasetId
						? (
							<ProjectHub
								datasetId={hubDatasetId}
								permissions={permissions}
								setUserPermission={setUserPermission}
								updateDatasetMetadata={updateDatasetMetadata}
								deleteProject={deleteProject}
								setEditingDataset={setEditingDataset}
								isOnline={isOnline}
								updateAreaProperty={updateAreaProperty} />
						)
						: (
							<Tabs
								id="setupPageTabs"
								activeKey={activeTab}
								onSelect={setActiveTab}
								animation={false}>
								<Tab eventKey={1} className="setupPageTab" title="Account Settings">
									<AccountSetup />
								</Tab>
								<Tab eventKey={2} className="setupPageTab" title="Project Configuration">
									<ProjectSetup datasetsLoading={datasetsLoading} />
								</Tab>
							</Tabs>
						)}
				</div>
			</div>
			<AddProjectModal shown={showAddProjectModal} />
			<EditProjectFileModal
				shown={editingDatasetId !== null}
				editingDatasetId={editingDatasetId}
				editingType={editingType}
				setEditingDataset={setEditingDataset} />
			{!isOnline || !tourSteps.length || !isTourOpen || hubDatasetId
				? null
				: <Tour
					steps={tourSteps}
					currentStep={currentTourStep}
					setCurrentStep={setCurrentTourStep}
					setIsOpen={handleSetTourOpen}
					onClickClose={closeTour}
					onClickMask={() => {}}
					disableInteraction={true}
					badgeContent={({ currentStep, totalSteps }) => `${currentStep + 1} of ${totalSteps}`}
					styles={{
						popover: (base) => ({
							...base,
							borderRadius: 6
						}),
						maskArea: (base) => ({
							...base,
							rx: 6
						})
					}}
					disabledActions={false}
					setDisabledActions={() => {}}
					setSteps={() => {}}
					meta=""
					setMeta={() => {}} />}
		</div>
	);
};

export default SetupPage;
